import { GptCoreError, TimeoutError } from "./errors";

/**
 * Security defaults for streaming
 */
const DEFAULT_STREAM_TIMEOUT = 300000; // 5 minutes
const DEFAULT_MAX_CHUNKS = 10000;
const DEFAULT_MAX_BUFFER_SIZE = 10 * 1024 * 1024; // 10MB

/**
 * Options for streaming requests
 */
export interface StreamOptions {
  signal?: AbortSignal;
  onError?: (error: Error) => void;
  timeout?: number;
  maxChunks?: number;
  maxBufferSize?: number;
}

/**
 * Events emitted by thread message streaming.
 *
 * For threads backed by an agent, the stream emits the full execution event
 * lifecycle including tool calls and results. For RAG threads (no agent),
 * only `content`/`token`, `done`, and `error` events are emitted.
 */
export type StreamMessageChunk =
  | StreamTokenEvent
  | StreamToolCallEvent
  | StreamToolResultEvent
  | StreamIterationCompleteEvent
  | StreamApprovalRequiredEvent
  | StreamDoneEvent
  | StreamErrorEvent;

/**
 * Incremental token from LLM streaming output.
 * `type: "content"` is a backward-compatible alias — new consumers should match on `"token"`.
 */
export interface StreamTokenEvent {
  type: "token" | "content";
  content?: string;
}

/** Tool invocation started (agent-backed threads only). */
export interface StreamToolCallEvent {
  type: "tool_call";
  data: { name: string; arguments: Record<string, unknown> };
}

/** Tool invocation completed (agent-backed threads only). */
export interface StreamToolResultEvent {
  type: "tool_result";
  data: { name: string; summary: string };
}

/** LLM loop iteration completed (agent-backed threads only). */
export interface StreamIterationCompleteEvent {
  type: "iteration_complete";
  data: { iteration: number; tokens: number };
}

/** Human-in-the-loop approval required (agent-backed threads only). */
export interface StreamApprovalRequiredEvent {
  type: "approval_required";
  data: {
    tool_name: string;
    arguments: Record<string, unknown>;
    classification: string;
  };
}

/** Metadata included with stream completion events. */
export interface StreamDoneMetadata {
  thread_id?: string;
  execution_id?: string;
  total_tokens?: number;
  [key: string]: unknown;
}

/** Stream completed successfully. */
export interface StreamDoneEvent {
  type: "done";
  content?: string;
  error?: string;
  metadata?: StreamDoneMetadata;
}

/** Stream error. */
export interface StreamErrorEvent {
  type: "error";
  content?: string;
  error?: string;
}

/**
 * Parse Server-Sent Events (SSE) stream into typed chunks.
 * Security: Enforces timeout, chunk count, and buffer size limits.
 */
export async function* streamSSE<T = unknown>(
  response: Response,
  options: StreamOptions = {},
): AsyncIterableIterator<T> {
  if (!response.body) {
    throw new GptCoreError("Response body is null", { code: "stream_error" });
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";

  const timeout = options.timeout ?? DEFAULT_STREAM_TIMEOUT;
  const maxChunks = options.maxChunks ?? DEFAULT_MAX_CHUNKS;
  const maxBufferSize = options.maxBufferSize ?? DEFAULT_MAX_BUFFER_SIZE;

  const startTime = Date.now();
  let chunkCount = 0;
  let bufferSize = 0;

  try {
    while (true) {
      const elapsed = Date.now() - startTime;
      if (elapsed > timeout) {
        reader.cancel();
        throw new TimeoutError(
          `Stream timeout exceeded after ${elapsed}ms (limit: ${timeout}ms)`,
        );
      }

      if (chunkCount >= maxChunks) {
        reader.cancel();
        throw new GptCoreError(`Maximum chunk limit exceeded (${maxChunks})`, {
          code: "stream_limit_exceeded",
        });
      }

      const { done, value } = await reader.read();

      if (done) break;

      if (options.signal?.aborted) {
        reader.cancel();
        throw new Error("Stream aborted");
      }

      bufferSize += value.length;
      if (bufferSize > maxBufferSize) {
        reader.cancel();
        throw new GptCoreError(
          `Stream buffer size exceeded (${bufferSize} bytes, limit: ${maxBufferSize})`,
          { code: "stream_limit_exceeded" },
        );
      }

      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split("\n");
      buffer = lines.pop() || "";

      for (const line of lines) {
        if (line.startsWith("data: ")) {
          const data = line.slice(6);
          if (data === "[DONE]" || data.trim() === "") continue;

          chunkCount++;

          try {
            yield JSON.parse(data) as T;
          } catch {
            yield {
              type: "error",
              error: `Malformed SSE data: ${data.substring(0, 200)}`,
            } as T;
          }
        }
      }
    }
  } catch (error) {
    if (options.onError) options.onError(error as Error);
    throw error;
  } finally {
    reader.releaseLock();
  }
}

/**
 * Parse streaming message response — stops on terminal events.
 */
export async function* streamMessage(
  response: Response,
  options: StreamOptions = {},
): AsyncIterableIterator<StreamMessageChunk> {
  for await (const chunk of streamSSE<StreamMessageChunk>(response, options)) {
    yield chunk;
    if (chunk.type === "done" || chunk.type === "error") break;
  }
}
